import { DatePicker, FlexAligner } from '@scaleaq/scaleui';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import { MultiChoiceChips } from '@/components/chips';
import { MultiChoiceChipsItem } from '@/components/chips/chips.types.ts';
import { Z_INDEX } from '@/constants';
import { responsive } from '@/utils';

import { filterClassLabelKey } from './fishObservationGallery/fishObservationGallery.functions.ts';
import { OrbitOneGalleryFilterClass } from './fishObservationGallery/fishObservationGallery.types';

interface OrbitOneImageGalleryFiltersProps {
  availableFilterClasses: OrbitOneGalleryFilterClass[];
  filters: OrbitOneGalleryFilterClass[];
  onFiltersChange: (filters: OrbitOneGalleryFilterClass[]) => void;
  date: DateTime;
  onDateChange: (date: DateTime) => void;
}

/** How far back the gallery date selector can be stepped, until a real data-retention bound exists. */
const galleryHistoryMonths = 12;

/** Toolbar for the observation gallery: lice-class filter chips and a date selector. */
export const OrbitOneImageGalleryFilters = ({
  availableFilterClasses,
  filters,
  onFiltersChange,
  date,
  onDateChange,
}: OrbitOneImageGalleryFiltersProps) => {
  const { t } = useTranslation('orbitOne');

  const now = DateTime.now();

  const filterChips: MultiChoiceChipsItem[] = availableFilterClasses.map(_class => ({
    id: _class,
    label: t(filterClassLabelKey({ filterClass: _class, availableFilterClasses })),
  }));

  return (
    <FlexAligner
      spaceBetween
      alignment='center'
      gap={responsive.spacing.large}
      wrap
      style={{ padding: `${responsive.spacing.large} ${responsive.spacing['4xlarge']}` }}
    >
      <MultiChoiceChips
        style={{ display: 'flex', flexWrap: 'wrap' }}
        checkMark
        items={filterChips}
        value={filters}
        onChange={next => {
          const updated = typeof next === 'function' ? next(filters) : next;
          onFiltersChange(updated as OrbitOneGalleryFilterClass[]);
        }}
      />
      <DatePicker
        daySwitcher
        date={date}
        minDate={now.minus({ months: galleryHistoryMonths })}
        maxDate={now.minus({ days: 1 })}
        width={'12rem'}
        onChange={newDate => newDate && onDateChange(newDate)}
        zIndex={Z_INDEX.overlay.error + 1}
      />
    </FlexAligner>
  );
};
